import type { TwiageCase } from "@/App/types/twiageTypes";
import { FeatureFlag } from "@/App/types/FeatureFlag";
import { useUserStore } from "@/App/stores";
import { useDispatchStore } from "@/App/components/Dispatch/dispatch.store";
import { CaseType } from "@/App/types/caseType";
import type { Ift } from "@/App/types/Ift";
import { CadNames } from "@/App/components/Cad/types/CadNames";

export enum IftEventType {
  closed = "closed",
  taken = "taken",
}

export const hasEmsAppUserLeftIftCase = (ift?: Ift): boolean => {
  if (!ift) return false;
  const events = [...(ift.events || [])].sort((a, b) => a.at - b.at);
  const closedEventIndex = events.findLastIndex((evt) => evt.type === IftEventType.closed);
  const takenEventIndex = events.findLastIndex((evt) => evt.type === IftEventType.taken);
  return closedEventIndex > takenEventIndex;
};

export const isIftCaseClosed = (twiageCase: TwiageCase): boolean => {
  return (
    twiageCase.type === CaseType.ift &&
    (twiageCase?.cadStatus === CadNames.inService || twiageCase.markedArrivedAt !== undefined)
  );
};

export const isIftDispatch = (): boolean => {
  return useUserStore().isFeatureEnabled(FeatureFlag.IftDispatch);
};

export const whoIsAssignedToIftCase = (twiageCase: TwiageCase): string | undefined => {
  if (twiageCase.truck) {
    return twiageCase.truck?.name;
  } else if (twiageCase.ift?.assigned?.candidateEmsUsers?.length) {
    const userId = twiageCase.ift.assigned.candidateEmsUsers[0];
    return useDispatchStore().getUser({ userId });
  }
  return undefined;
};

export const isHospitalSetToSource = (twiageCase: TwiageCase): boolean => {
  return twiageCase.destinationHospital?.id === twiageCase?.ift?.source?.hospital?.id;
};

export const isHospitalSetToDestination = (twiageCase: TwiageCase): boolean => {
  return twiageCase.destinationHospital?.id === twiageCase?.ift?.destination?.hospital?.id;
};

export const isCrewSelected = (twiageCase: TwiageCase): boolean => {
  return (twiageCase.ift?.assigned?.candidateEmsUsers?.length ?? 0) > 0;
};

export const isCrewAssigned = (twiageCase: TwiageCase): boolean => {
  return twiageCase.emsAgency !== undefined && twiageCase.truck !== undefined;
};

export const isPatientDelivered = (twiageCase: TwiageCase): boolean => {
  if (twiageCase.ift) {
    return isHospitalSetToDestination(twiageCase) && isIftCaseClosed(twiageCase);
  }
  return false;
};

export const isEmsEnroutePickup = (twiageCase: TwiageCase): boolean => {
  if (twiageCase.ift) {
    return (
      !hasEmsAppUserLeftIftCase(twiageCase.ift) &&
      !isPatientDelivered(twiageCase) &&
      isCrewAssigned(twiageCase) &&
      isHospitalSetToSource(twiageCase)
    );
  }
  return false;
};

export const isEmsEnrouteDelivery = (twiageCase: TwiageCase): boolean => {
  if (twiageCase.ift) {
    return (
      !hasEmsAppUserLeftIftCase(twiageCase.ift) &&
      !isPatientDelivered(twiageCase) &&
      isCrewAssigned(twiageCase) &&
      isHospitalSetToDestination(twiageCase)
    );
  }
  return false;
};
